// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

function hoursToHm(val: number): string {
  if (val == null || isNaN(val)) return '--';
  const h = Math.floor(val);
  const m = Math.round((val - h) * 60);
  if (m === 60) return `${h + 1}h 0m`;
  return `${h}h ${m}m`;
}

function mtbfCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  const num = Number(val);
  let color = '#dc2626'; let fontWeight = 400;
  if (num >= 400)      { color = '#16a34a'; fontWeight = 600; }
  else if (num >= 200) { color = '#d97706'; }
  return <span style={{ color, fontWeight }}>{hoursToHm(num)}</span>;
}

function mttrCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  const num = Number(val);
  let color = '#16a34a'; let fontWeight = 400;
  if (num > 72)       { color = '#dc2626'; fontWeight = 600; }
  else if (num > 24)  { color = '#d97706'; }
  return <span style={{ color, fontWeight }}>{hoursToHm(num)}</span>;
}

function availabilityCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  const num = Number(val);
  let color = '#dc2626'; let fontWeight = 400;
  if (num >= 95)      { color = '#16a34a'; fontWeight = 600; }
  else if (num >= 85) { color = '#d97706'; }
  return <span style={{ color, fontWeight }}>{num.toFixed(2)}%</span>;
}

const MtbfReport: React.FC = () => {
  const assetsApi = useAssetsApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    assetsApi.getMttrReportFilters(
      (response: any) => {
        const d = response?.data || response;
        setFilterOptions([
          { key: 'facility',    label: 'Facility',     options: (d?.facilities    || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'subFacility', label: 'Sub Facility', options: (d?.subFacilities || []).filter((f: any) => f.name?.trim()).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'assetGroup',  label: 'Asset Group',  options: (d?.assetGroups   || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'department',  label: 'Department',   options: (d?.departments   || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'status',      label: 'Status',       options: (d?.statuses      || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
        ]);
      },
      () => {}
    );
  }, []);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility)    params.FacilityIDs    = filterParams.facility;
    if (filterParams.subFacility) params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup)  params.AssetGroupIDs  = filterParams.assetGroup;
    if (filterParams.department)  params.DepartmentIDs  = filterParams.department;
    if (filterParams.status)      params.StatusIDs      = filterParams.status;

    assetsApi.getMtbfReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setTotalRecords(d?.totalRecord || 0);
      },
      (error: any) => { console.error('Failed to fetch MTBF report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>) => {
    setFilters(filterMap);
    setCurrentPage(1);
    fetchData(1, filterMap);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility)    params.FacilityIDs    = filters.facility;
    if (filters.subFacility) params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup)  params.AssetGroupIDs  = filters.assetGroup;
    if (filters.department)  params.DepartmentIDs  = filters.department;
    if (filters.status)      params.StatusIDs      = filters.status;
    setIsExporting(true);
    assetsApi.exportMtbfReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'mtbf_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Asset Name',        field: 'assetName',       headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell,               headerTooltip: 'Asset Name' },
    { headerName: 'Asset ID',          field: 'assetCode',       headerClass: 'bg-[#f0f0f0]', width: 160,            cellRenderer: titleCell,               headerTooltip: 'Asset Code' },
    { headerName: 'Facility',          field: 'facilityName',    headerClass: 'bg-[#f0f0f0]', width: 180,            cellRenderer: titleCell,               headerTooltip: 'Facility' },
    { headerName: 'Sub Facility',      field: 'subFacilityName', headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,               headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group',       field: 'assetGroupName',  headerClass: 'bg-[#f0f0f0]', width: 150,            cellRenderer: titleCell,               headerTooltip: 'Asset Group' },
    { headerName: 'Department',        field: 'departmentName',  headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,               headerTooltip: 'Department' },
    { headerName: 'No. Failures',      field: 'noFailures',      headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned',                            headerTooltip: 'Number of Failures' },
    { headerName: 'First Failure',     field: 'firstFailureDate',headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,               headerTooltip: 'First Failure Date' },
    { headerName: 'Last Failure',      field: 'lastFailureDate', headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,               headerTooltip: 'Last Failure Date' },
    { headerName: 'Total Downtime',    field: 'totalDowntime',   headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: mttrCellRenderer, headerTooltip: 'Total Downtime' },
    { headerName: 'MTTR',              field: 'mttr',            headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: mttrCellRenderer, headerTooltip: 'Mean Time To Repair = Total Downtime ÷ No. Failures' },
    { headerName: 'MTBF',              field: 'mtbf',            headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: mtbfCellRenderer, headerTooltip: 'Mean Time Between Failures = Total Operational Time ÷ No. Failures' },
    { headerName: 'Availability',      field: 'availability',    headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: availabilityCellRenderer, headerTooltip: 'Availability = MTBF ÷ (MTBF + MTTR) × 100' },
  ];

  return (
    <Layout>
      <Header title="MTBF Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} showDateRange={false} onApply={handleApplyFilter} />
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
        <p className="px-4 pb-3 mt-2 text-[11px] text-gray-400">
          All-time metrics from asset first online date. MTTR = Total Downtime ÷ No. Failures &nbsp;·&nbsp; MTBF = Total Operational Time ÷ No. Failures &nbsp;·&nbsp; Availability = MTBF ÷ (MTBF + MTTR) × 100.
        </p>
      </div>
    </Layout>
  );
};

export default MtbfReport;
